import type { ProjectDetail, ProjectTask, PhotoItem, Collector } from '@/types/odoo';
import { SafeHtml } from '@/components/ui/SafeHtml';
import { buildCollectorenAggregate } from '@/utils/warehouseCollectoren';
import { initialsOf, mapsUrl } from '@/utils/warehouse';

interface WerfTaakTabProps {
  detail: ProjectDetail;
  collectors: Collector[];
  plans: PhotoItem[];
  faseTaskId: number | null;
  werfAddress: string | null;
}

const WEEKDAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

function formatTaskDate(deadline: string | false): string | null {
  if (!deadline) return null;
  const datePart = String(deadline).slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${WEEKDAYS[date.getDay()]} ${pad(d)}/${pad(m)}`;
}

function hasText(html: string | false | undefined): boolean {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
}

/** Surface the FIRST planning task of the fase — the chronologically earliest scheduled
 *  child (lowest date_deadline). Falls back to any child, then the fase task itself. */
function pickPlanningTask(tasks: ProjectTask[], faseTaskId: number | null): ProjectTask | null {
  if (faseTaskId === null) return tasks[0] ?? null;
  const children = tasks.filter((t) => t.parent_id && t.parent_id[0] === faseTaskId);
  const scheduled = children
    .filter((t) => t.date_deadline)
    .sort((a, b) => String(a.date_deadline).localeCompare(String(b.date_deadline)));
  if (scheduled.length > 0) return scheduled[0];
  if (children.length > 0) return children[0];
  return tasks.find((t) => t.id === faseTaskId) ?? null;
}

function SpecTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-white p-3 shadow-sm border border-gray-100">
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">{label}</p>
      <p className="mt-0.5 text-sm font-extrabold text-td-navy">{value}</p>
    </div>
  );
}

function PersonRow({ label, name, accent }: { label: string; name: string; accent: 'blue' | 'green' }) {
  const ring = accent === 'blue' ? 'bg-td-blue/15 text-td-blue' : 'bg-td-green/15 text-td-green';
  return (
    <div className="px-3.5 py-2.5 flex items-center gap-2 border-t border-gray-50">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${ring}`}>
        <span className="text-[10px] font-bold">{initialsOf(name)}</span>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-td-muted leading-none">{label}</p>
        <p className="text-sm font-semibold text-td-navy truncate">{name}</p>
      </div>
    </div>
  );
}

export function WerfTaakTab({ detail, collectors, plans, faseTaskId, werfAddress }: WerfTaakTabProps) {
  const agg = buildCollectorenAggregate(collectors);
  const planningTask = pickPlanningTask(detail.projectTasks, faseTaskId);

  const systemsLabel =
    agg.systems.length > 0
      ? agg.systems
          .map((s) => (s.systeem ? s.systeem.charAt(0).toUpperCase() + s.systeem.slice(1) : 'Systeem ?'))
          .join(' + ')
      : '—';

  const verdiepingen = Array.from(
    new Set(collectors.map((c) => c.x_studio_verdieping).filter((v): v is string => !!v)),
  ).sort();

  const dateLabel = formatTaskDate(planningTask?.date_deadline ?? false);
  const address = werfAddress || detail.werfLocation || detail.werfContact?.address || null;

  // Resolve installer name from the planning task's installateurs (best effort).
  const installerName = (() => {
    const ids = planningTask?.x_studio_installateur;
    if (ids && ids.length > 0) {
      const first = detail.installerNames[String(ids[0])];
      if (first) return ids.length > 1 ? `${first} +${ids.length - 1}` : first;
    }
    return null;
  })();

  return (
    <div className="space-y-3">
      {/* Read-only banner */}
      <div className="rounded-lg bg-gray-100 px-3 py-1.5 flex items-center gap-1.5 text-[11px] text-td-muted">
        <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        Alleen-lezen · zoals de installateur het ziet
      </div>

      {/* Spec tiles */}
      <div className="grid grid-cols-2 gap-2">
        <SpecTile label="Systemen" value={systemsLabel} />
        <SpecTile label="Oppervlakte (Σ)" value={`${Math.round(agg.totalM2)} m²`} />
        <SpecTile label="Verdiepingen" value={verdiepingen.length > 0 ? verdiepingen.join(' · ') : '—'} />
        <SpecTile label="Datum" value={dateLabel ?? '—'} />
      </div>

      {/* Werf & contact */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-3.5 py-2.5 border-b border-gray-100">
          <p className="text-sm font-bold text-td-navy">Werf &amp; contact</p>
        </div>
        <div className="px-3.5 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Werfadres</p>
            <p className="mt-0.5 text-sm font-semibold text-td-navy truncate">{address || 'Niet bekend'}</p>
          </div>
          {address && (
            <a
              href={mapsUrl(address)}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-td-blue/10 text-td-blue text-[11px] font-bold"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Kaart
            </a>
          )}
        </div>
        {detail.projectleiderName && (
          <PersonRow label="Projectleider" name={detail.projectleiderName} accent="blue" />
        )}
        <PersonRow label="Installateur" name={installerName ?? 'Niet toegewezen'} accent="green" />
      </div>

      {/* Task description */}
      {planningTask && hasText(planningTask.description) && (
        <div className="rounded-xl bg-white p-3.5 shadow-sm border border-gray-100">
          <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Taakomschrijving</p>
          <SafeHtml
            html={planningTask.description as string}
            className="mt-2 text-sm text-td-navy leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
          />
        </div>
      )}

      {/* Plans / files */}
      {plans.length > 0 && (
        <div className="rounded-xl bg-white shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-3.5 py-2.5 border-b border-gray-100">
            <p className="text-sm font-bold text-td-navy">Plannen &amp; bestanden</p>
          </div>
          <div className="divide-y divide-gray-50">
            {plans.map((plan) => {
              const filename = plan.filename || plan.key.split('/').pop() || plan.key;
              return (
                <div key={plan.key} className="flex items-center gap-3 px-3.5 py-2.5">
                  <div className="w-8 h-8 rounded-lg bg-td-magenta/10 flex items-center justify-center shrink-0">
                    <span className="text-[9px] font-bold text-td-magenta">PDF</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-td-navy truncate">{filename}</p>
                  </div>
                  <a
                    href={plan.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-[11px] font-bold text-td-blue"
                  >
                    Bekijken
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
